/**
 * Typed preference selectors (slices.yaml preferences notes: other slices read preferences through these, never by
 * parsing `user_settings` themselves). Every read is a tracked store read, so an `observer` component or a MobX
 * reaction that calls one re-runs when the setting changes on this or another client (realtime ACs: < 1 s).
 */
import { readPreferences, type Preferences } from '@quadratic/contracts/identity/preferences/prefs';
import type { Row, Store } from '@quadratic/sync-client';

/** The signed-in user's own `UserSettings` row (eager, `[u:U]`), or undefined before boot. */
export function mySettingsRow(store: Store): Row | undefined {
  if (!store.userId) return undefined;
  return store.where('UserSettings', 'userId', store.userId)[0];
}

/** The signed-in user's preferences with defaults (a missing row reads as a new user's defaults). */
export function myPreferences(store: Store | null | undefined): Preferences {
  return readPreferences(store ? (mySettingsRow(store) ?? null) : null);
}

/** A person's label per the viewer's "Display full names" (display-full-names AC1/AC2). */
export function userLabel(user: { name?: unknown; displayName?: unknown } | null | undefined, prefs: Pick<Preferences, 'showFullUserNames'>): string {
  if (!user) return '';
  const full = typeof user.name === 'string' ? user.name : '';
  const short = typeof user.displayName === 'string' ? user.displayName : '';
  return (prefs.showFullUserNames ? full || short : short || full) || '';
}

/**
 * Whether a person matches a picker query: name and username both match whichever is displayed
 * (display-full-names AC6: 'Lindqvist' finds Bo with full names off).
 */
export function userMatches(user: { name?: unknown; displayName?: unknown }, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [user.name, user.displayName].filter((s): s is string => typeof s === 'string').map((s) => s.toLowerCase());
  return hay.some((s) => s.includes(q));
}

/**
 * The assignee to prefill in the create-issue composer (auto-assign AC1–AC3): the viewer when "Auto-assign to self"
 * is on, else null. Create-time auto-assign is client-side only, so API and import creates are never auto-assigned.
 */
export function createAssigneeDefault(store: Store): string | null {
  return myPreferences(store).autoAssignToSelf && store.userId ? store.userId : null;
}

/**
 * The optimistic half of assign-on-start (auto-assign AC4–AC6): merge into the issue update that changes `stateId`
 * so the prediction matches what the server does (the server applies the same rule itself, so a client that skips
 * this only sees the assignee a moment later). Returns `{ assigneeId }` to add, or null.
 */
export function assignOnStartPatch(store: Store, issue: { assigneeId?: unknown } | null | undefined, nextStateId: string, explicitAssignee = false): { assigneeId: string } | null {
  if (explicitAssignee || !issue || (issue.assigneeId !== null && issue.assigneeId !== undefined)) return null;
  if (!store.userId || !myPreferences(store).assignOnStart) return null;
  const state = store.get('WorkflowState', nextStateId);
  return state?.type === 'started' ? { assigneeId: store.userId } : null;
}
